"use client";

import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@ospweb/sdk-ui";
import type { Web3LoginSupportedType } from "@features/auth/compat/sdk-types";
import { cn, deviceDetector } from "utils";
import { LoadingButton } from "../LoadingButton";
import type { EmailLoginVariant } from "../types";

const SOCIAL_ICONS: {
  type: Web3LoginSupportedType;
  iconName: string;
}[] = [
  { type: "APPLE", iconName: "apple" },
  { type: "GOOGLE", iconName: "Google with colour" },
];

const BUTTON_CLASS: Record<EmailLoginVariant, string> = {
  desktop:
    "flex-1 h-12 rounded-lg flex items-center justify-center bg-background_white_overlay10",
  popup:
    "flex-1 h-12 rounded-lg flex items-center justify-center bg-[#043b421a]",
  mobile:
    "flex h-12 min-w-0 flex-1 items-center justify-center rounded-2 border border-solid border-border_solid bg-background_solid_neutral_primary",
};

const ROW_CLASS: Record<EmailLoginVariant, string> = {
  desktop: "flex gap-4",
  popup: "flex gap-4",
  mobile: "flex w-full gap-3",
};

interface SocialLoginButtonsProps {
  variant: EmailLoginVariant;
  onSocialClick: (type: Web3LoginSupportedType) => void;
  socialLoading?: boolean;
  className?: string;
  /** V3 Sign in：社交按钮圆角 8px（rounded-lg） */
  useV301Chrome?: boolean;
}

export function SocialLoginButtons({
  variant,
  onSocialClick,
  socialLoading = false,
  className,
  useV301Chrome = false,
}: SocialLoginButtonsProps) {
  const [activeSocialType, setActiveSocialType] =
    useState<Web3LoginSupportedType | null>(null);
  const [isAndroidClickLoading, setIsAndroidClickLoading] = useState(false);
  const androidClickTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const socialLoadingWasTrue = useRef(false);
  const mergedLoading = socialLoading || isAndroidClickLoading;

  useEffect(() => {
    if (socialLoading) {
      socialLoadingWasTrue.current = true;
    }
    if (
      !socialLoading &&
      socialLoadingWasTrue.current &&
      isAndroidClickLoading
    ) {
      setIsAndroidClickLoading(false);
      socialLoadingWasTrue.current = false;
      if (androidClickTimerRef.current) {
        clearTimeout(androidClickTimerRef.current);
      }
    }
  }, [socialLoading, isAndroidClickLoading]);

  const handleClick = (
    type: Web3LoginSupportedType,
    _e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setActiveSocialType(type);
    const hasBridgeLogin =
      typeof (window as Window & { bridge?: { login?: () => void } }).bridge
        ?.login === "function";
    const useDelayedClick =
      variant === "mobile"
        ? deviceDetector.isAndroidApp() || hasBridgeLogin
        : deviceDetector.isAndroidApp() ||
          (hasBridgeLogin && !deviceDetector.isIOSApp());

    if (useDelayedClick) {
      setIsAndroidClickLoading(true);
      if (androidClickTimerRef.current) {
        clearTimeout(androidClickTimerRef.current);
      }
      androidClickTimerRef.current = setTimeout(() => {
        setIsAndroidClickLoading(false);
      }, 10_000);
      setTimeout(() => {
        onSocialClick(type);
      }, 500);
      return;
    }

    onSocialClick(type);
  };

  return (
    <div className={cn(ROW_CLASS[variant], className)}>
      {SOCIAL_ICONS.map(({ type, iconName }) => (
        <LoadingButton
          data-openreplay-label={`login.socialloginbuttons.${type.toLowerCase()}`}
          key={type}
          type="button"
          loading={mergedLoading && activeSocialType === type}
          disabled={mergedLoading}
          onClick={(e) => handleClick(type, e)}
          childrenClassName="h-6"
          className={cn(
            BUTTON_CLASS[variant],
            variant === "mobile" && useV301Chrome && "rounded-lg",
          )}
          aria-label={type}
        >
          <Icon
            name={iconName as "apple" | "Google with colour"}
            size={24}
            className="text-content_primary"
          />
        </LoadingButton>
      ))}
    </div>
  );
}
